export const UUID_REGEX =
    /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;
export const LOWERCASE_UUID_REGEX =
    /^[0-9a-f]{8}\b-[0-9a-f]{4}\b-[0-9a-f]{4}\b-[0-9a-f]{4}\b-[0-9a-f]{12}$/;

export function isValidUUID(value: string): boolean {
    return UUID_REGEX.test(value);
}

export function isValidLowercaseUUID(value: string): boolean {
    return LOWERCASE_UUID_REGEX.test(value);
}

/**
 * Generates a deterministic "pseudo-GUID" based on the provided input.
 *
 * @param input - The input string or array of strings to generate the "pseudo-GUID" from.
 * @param lowercase - (Optional) If true, the generated "pseudo-GUID" will be in lowercase. Default is false (uppercase).
 * @returns A string representing the "pseudo-GUID"
 * @throws Error if the input is empty, null, or undefined.
 */
export function generateDeterministicGuid(
    input: string | string[],
    lowercase = false
): string {
    if (
        input === null ||
        input === undefined ||
        (Array.isArray(input) && input.length === 0) ||
        (typeof input === 'string' && input.trim() === '')
    ) {
        throw new Error('Input cannot be empty, null, or undefined.');
    }

    let inputStr = Array.isArray(input) ? input.join(',') : input;

    // Ensure a consistent minimum input length by repeating it if it's too short.
    const minLength = 32; // The desired minimum length
    if (inputStr.length < minLength) {
        const repeatCount = Math.ceil(minLength / inputStr.length);
        inputStr = inputStr.repeat(repeatCount).substring(0, minLength);
    }

    // Custom hash function to create a "pseudo-GUID", used to avoid dependency on CryptoJS
    let hash = 0;
    for (let i = 0; i < inputStr.length; i++) {
        const char = inputStr.charCodeAt(i);
        hash = (hash << 5) - hash + char;
    }

    // Convert the hash to a hexadecimal string.
    const hashHex = (hash >>> 0).toString(16);

    // Pad with leading zeros to ensure a length of 32 characters.
    const paddedHash = '0'.repeat(32 - hashHex.length) + hashHex;

    // Format as a "pseudo-GUID."
    const guid = `${paddedHash.substring(0, 8)}-${paddedHash.substring(
        8,
        12
    )}-${paddedHash.substring(12, 16)}-${paddedHash.substring(
        16,
        20
    )}-${paddedHash.substring(20, 32)}`;

    return lowercase ? guid.toLowerCase() : guid;
}
